import { Card, H5, Icon, Intent, Menu, MenuItem, Tag } from "@blueprintjs/core";
import { ContextMenu2 } from "@blueprintjs/popover2";
import { useCallback, useContext } from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { InstructionTo, ListState } from "src/models/TemplateCreationList";
import { UserProject } from "src/models/UserProjects";
import { NewTemplateInstructionsContext, SourceDialogCurrentProjectContext } from "src/utils/context";
import { Dictionary } from "src/utils/utils";

type Props = {
    lists: ListState;
    setLists: React.Dispatch<React.SetStateAction<ListState>>;
    list: InstructionTo[];
    setList: (listName: keyof ListState, newList: InstructionTo[]) => void;
    availableInstructions: UserProject[];
};

export default function InputDependecySet({ lists, setLists, list, setList, availableInstructions }: Props) {
    const [currentProject] = useContext(SourceDialogCurrentProjectContext);
    const [instructions] = useContext(NewTemplateInstructionsContext);
    const getAllAvailableOutputs = useCallback(() => {
        let outputs: Dictionary<{ source: string }> = {};
        if (currentProject) {
            const clickedOnInstruction = instructions.find(i => i.projectId === currentProject.projectId);
            if (clickedOnInstruction) {
                clickedOnInstruction.dependsOn.forEach(dependencyId => {
                    // dependencies use instructionId but to get project outputs we
                    // need to find what project that instruction is referencing
                    const dependencyInstruction = instructions.find(i => i.instructionId === dependencyId);
                    const proj = availableInstructions.find(a => a.projectId === dependencyInstruction?.projectId);
                    if (proj?.outputs) {
                        Object.keys(proj?.outputs).forEach(o => {
                            outputs[o] = { source: dependencyId! };
                        });
                    }
                });
            }
        }
        return outputs;
    }, [availableInstructions, currentProject, instructions]);

    return (
        <Card className="instructions-card">
            <H5>Dependecy Result</H5>
            <Droppable droppableId={`dependency`}>
                {provided => (
                    <ul style={{ listStyle: "none" }} {...provided.droppableProps} ref={provided.innerRef}>
                        {lists.dependency.map((p, index) => {
                            return (
                                <Draggable
                                    key={`dependency_${p.original}_${index}`}
                                    draggableId={`dependency_${p}_${index}`}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <li
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            ref={provided.innerRef}
                                        >
                                            <ContextMenu2
                                                content={
                                                    <Menu>
                                                        <MenuItem
                                                            key={`mapto_${p.original}_${index}`}
                                                            icon="new-link"
                                                            text="Map To Output"
                                                        >
                                                            {Object.entries(getAllAvailableOutputs()).map(
                                                                ([key, value]) => (
                                                                    <MenuItem
                                                                        key={`output_${key}`}
                                                                        text={key}
                                                                        onClick={() => {
                                                                            const newList = [...list];
                                                                            newList[index].mapsTo = key;
                                                                            newList[index].source = value.source;
                                                                            setList("dependency", newList);
                                                                        }}
                                                                    />
                                                                )
                                                            )}
                                                        </MenuItem>
                                                        <MenuItem
                                                            key={`remove_map_${p.original}_${index}`}
                                                            icon="cross"
                                                            text="Remove Mapping"
                                                            intent={Intent.DANGER}
                                                            disabled={p.mapsTo ? false : true}
                                                            onClick={() => {
                                                                const newList = [...list];
                                                                newList[index].mapsTo = undefined;
                                                                newList[index].source = undefined;
                                                                setList("dependency", newList);
                                                            }}
                                                        ></MenuItem>
                                                    </Menu>
                                                }
                                            >
                                                <div className="dependecy-tag">
                                                    <Tag
                                                        key={`tag_${p.original}_${index}`}
                                                        intent={snapshot.isDragging ? Intent.PRIMARY : Intent.NONE}
                                                    >
                                                        {p.original}
                                                    </Tag>
                                                    {p.mapsTo && (
                                                        <>
                                                            <Icon icon="arrow-right" intent={Intent.SUCCESS} />
                                                            <Tag
                                                                key={`tag_${p.mapsTo}_${index}`}
                                                                intent={
                                                                    snapshot.isDragging ? Intent.PRIMARY : Intent.NONE
                                                                }
                                                            >
                                                                {p.mapsTo}
                                                            </Tag>
                                                        </>
                                                    )}
                                                </div>
                                            </ContextMenu2>
                                        </li>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </ul>
                )}
            </Droppable>
        </Card>
    );
}
